import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/shared/PageHeader';
import { WorkTile } from '../components/shared/WorkTile';
import { Lightbox } from '../components/shared/Lightbox';
import { Reveal } from '../components/shared/Reveal';
import { CtaBand } from '../components/shared/CtaBand';
import { useLanguage } from '../contexts/LanguageContext';
import { useProjects } from '../hooks/useProjects';
import { useSeo } from '../hooks/useSeo';
import type { PortfolioCategory } from '../data/site';
const categories: PortfolioCategory[] = ['weddings', 'balloons', 'celebrations', 'centerpieces', 'themed', 'corporate'];
export function Portfolio() {
  const {
    t,
    lang
  } = useLanguage();
  const page = t.portfolio.page;
  const projects = useProjects();
  const [params, setParams] = useSearchParams();
  const [index, setIndex] = useState<number | null>(null);
  useSeo({
    ...t.common.seo.portfolio,
    lang
  });
  const raw = params.get('category');
  const active = categories.includes(raw as PortfolioCategory) ? raw as PortfolioCategory : 'all';
  const shown = useMemo(() => {
    const list = active === 'all' ? projects : projects.filter(p => p.categories.includes(active));
    return [...list.filter(p => p.image), ...list.filter(p => !p.image)];
  }, [projects, active]);
  const viewable = shown.filter(p => p.image);
  const select = (value: PortfolioCategory | 'all') => {
    setIndex(null);
    setParams(value === 'all' ? {} : {
      category: value
    }, {
      replace: true
    });
  };
  const filters: (PortfolioCategory | 'all')[] = ['all', ...categories];
  return <main data-id="element-478" data-mp="1yzar5zf5qxpb">
      <PageHeader eyebrow={page.eyebrow} title={page.title} description={page.description} data-id="element-479" data-mp="1yzkgrlwaj2uh">
        <div role="group" aria-label={page.filterLabel} className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:flex-wrap sm:px-0" data-id="element-480" data-mp="23xcc6pw5973f">
          {filters.map(f => {
          const on = active === f;
          return <button key={f} type="button" onClick={() => select(f)} aria-pressed={on} className={`h-11 shrink-0 whitespace-nowrap rounded-full border px-5 text-sm transition-colors duration-150 ease-smooth ${on ? 'border-mauve bg-mauve text-white' : 'border-petal bg-white text-ink/75 hover:border-mauve/40 hover:text-mauve'}`} data-id="element-481" data-mp="1jwwxv9q0zyk">
                {t.portfolio.filters[f]}
              </button>;
        })}
        </div>
      </PageHeader>

      <section className="bg-white py-14 sm:py-16" data-id="element-482" data-mp="u719clb58gkd">
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-483" data-mp="p8g3a597n2o0">
          <p className="text-sm text-ink/55" aria-live="polite" data-id="element-484" data-mp="pb2c087u7o7t">
            {shown.length} {page.count}
          </p>
          {shown.length > 0 ? <div className="mt-8 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-485" data-mp="z7yloh9h2gdy">
              {shown.map((project, i) => <Reveal key={`${active}-${project.id}`} delay={0.03 * (i % 3)} className="h-full" data-id="element-486" data-mp="1iql0qn75xvw2" data-mpi={i}>
                  <WorkTile project={project} viewLabel={t.home.work.viewProject} onOpen={() => setIndex(viewable.findIndex(v => v.id === project.id))} data-id="element-487" data-mp="1ms6l0tm11cga" />
                </Reveal>)}
            </div> : <p className="mt-8 rounded-2xl bg-cream p-10 text-center text-ink/60" data-id="element-488" data-mp="z2lsbj02n0tt">{t.portfolio.empty}</p>}
        </div>
        <Lightbox items={viewable} index={index} onClose={() => setIndex(null)} onChange={setIndex} data-id="element-489" data-mp="q49y76zzxkwm" />
      </section>

      <CtaBand data-id="element-490" data-mp="v2bgvqtifi7k" />
    </main>;
}